import React from "react";
import type { CanonicalCommentWorkspaceSurface } from "../model/types";
import styles from "../CanonicalCommentWorkspace.module.css";

type WorkspaceShellProps = {
  surface: CanonicalCommentWorkspaceSurface;
  title?: string;
  subtitle?: string;
  loading?: boolean;
  error?: unknown;
  onRequestExpand?: () => void;
  onRequestDock?: () => void;
  onRequestClose?: () => void;
  children: React.ReactNode;
};

const HERO_GUIDE_ITEMS = [
  {
    label: "Read",
    text: "Branch mode keeps the selected comment, its path, siblings, and children visible together.",
  },
  {
    label: "Navigate",
    text: "Use Overview, Lanes, Map, and Recent when the discussion becomes too large for linear reading.",
  },
  {
    label: "Return",
    text: "Dock the workspace when you want to keep the Gist open and continue reading the article.",
  },
];

export const WorkspaceShell: React.FC<WorkspaceShellProps> = ({
  surface,
  title,
  subtitle,
  loading = false,
  error,
  onRequestExpand,
  onRequestDock,
  onRequestClose,
  children,
}) => {
  const isExpanded = surface === "expanded";
  const shellClassName = `${styles.workspaceShell} ${
    isExpanded ? styles.workspaceShellExpanded : styles.workspaceShellDocked
  }`;
  const headerClassName = `${styles.workspaceHeader} ${isExpanded ? styles.workspaceHeaderExpanded : ""}`;

  const titleBlockChildren: React.ReactNode[] = [
    React.createElement(
      "span",
      { key: "kicker", className: styles.workspaceKicker },
      subtitle || "Canonical Comment Workspace",
    ),
    React.createElement("h2", { key: "title" }, title || "Discussion"),
  ];

  if (isExpanded) {
    titleBlockChildren.push(
      React.createElement(
        "p",
        { key: "subline", className: styles.workspaceHeroSubline },
        "Focused discussion workspace for deep reading, branch navigation, anchored context, and replies.",
      ),
    );
  }

  const actionChildren: React.ReactNode[] = [
    loading
      ? React.createElement("span", { key: "status", className: styles.workspaceStatus }, "Syncing…")
      : null,
    !isExpanded && onRequestExpand
      ? React.createElement(
          "button",
          { key: "expand", type: "button", onClick: onRequestExpand },
          "Expand workspace",
        )
      : null,
    isExpanded && onRequestDock
      ? React.createElement("button", { key: "dock", type: "button", onClick: onRequestDock }, "Dock panel")
      : null,
    onRequestClose
      ? React.createElement(
          "button",
          {
            key: "close",
            type: "button",
            className: styles.workspaceCloseButton,
            onClick: onRequestClose,
          },
          "Close",
        )
      : null,
  ];

  const shellChildren: React.ReactNode[] = [
    React.createElement(
      "header",
      { key: "header", className: headerClassName },
      [
        React.createElement("div", { key: "title-block", className: styles.workspaceTitleBlock }, titleBlockChildren),
        React.createElement("div", { key: "actions", className: styles.workspaceHeaderActions }, actionChildren),
      ],
    ),
    isExpanded
      ? React.createElement(
          "div",
          {
            key: "guide",
            className: styles.workspaceHeroGuide,
            "aria-label": "Expanded workspace guide",
          },
          HERO_GUIDE_ITEMS.map((item) =>
            React.createElement(
              "div",
              { key: item.label, className: styles.workspaceHeroGuideItem },
              [React.createElement("strong", { key: "label" }, item.label), React.createElement("span", { key: "text" }, item.text)],
            ),
          ),
        )
      : null,
    error
      ? React.createElement(
          "div",
          { key: "error", className: styles.workspaceError, role: "status" },
          "Comments are temporarily unavailable. The Gist remains readable.",
        )
      : null,
    React.createElement(React.Fragment, { key: "content" }, children),
  ];

  return React.createElement(
    "section",
    {
      className: shellClassName,
      "aria-label": "Canonical Comment Workspace",
    },
    React.createElement("div", { className: styles.workspaceShellInner }, shellChildren),
  );
};
